// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { ellipsis, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
    // XNG-Changes
    view?: ProductListViewType;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: auto;
            align-items: flex-start;
            align-self: baseline;

            ${({ view }: any) =>
                view === "Grid" &&
                css`
                    min-height: 34px;
                    ${xScreen("lte", "xs")} {
                        min-height: initial;
                    }
                `}
        `,
        typographyProps: {
            size: 15,
            weight: "bold",
            css: css`
                width: auto;
                letter-spacing: 0;
                line-height: 20px;
                ${ellipsis(2)}
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription = ({ product, onClick, view, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeWithCss(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            {...{ view }}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default ProductDescription;
